import React, { useEffect, useRef, useState } from "react";
import { Label, Radio, RadioGroup } from "@fluentui/react-components";
import { useRawMaterialControl } from "../redux/ReduxContext";
import CustomSelect from "../CustomSelect";

type MillSize = "0.25" | "custom";

type MillingPreset = {
    grindingBodies: string;
    grindTimeHours: number;
    grindTimeMinutes: number;
    alcohol: string;
};

const MILLING_PRESETS: Record<Exclude<MillSize, "custom">, MillingPreset> = {
    "0.25": {
        grindingBodies: "3",
        grindTimeHours: 10,
        grindTimeMinutes: 0,
        alcohol: "0,2",
    },
};

const CUSTOM_MILLING_DEFAULTS: MillingPreset = {
    grindingBodies: "3",
    grindTimeHours: 10,
    grindTimeMinutes: 0,
    alcohol: "0,2",
};

const GRINDING_BODIES_TYPES = ["PS181 sort 465", "PS181 sort 477"];

const cardClass = "bg-white dark:bg-oi-dark-background p-4 mb-4 drop-shadow-md";
const fieldClassName =
    "h-10 w-full border border-gray-300 dark:border-gray-600 bg-primary-light dark:bg-primary-dark px-3 focus:outline-none min-w-[56px]";
const fieldLabelClass =
    "mb-1 block text-[11px] font-semibold text-primary-dark dark:text-primary-light whitespace-nowrap";

function normalizeDecimalValue(value: number | string | undefined) {
    return String(value ?? "").replace(".", ",");
}

function getMillingMode(value: string | number | undefined): MillSize {
    const normalizedValue = value?.toString();
    return normalizedValue === "0.25" ? "0.25" : "custom";
}

function getMillingDefaults(millSize: MillSize): MillingPreset {
    return millSize === "custom" ? CUSTOM_MILLING_DEFAULTS : MILLING_PRESETS[millSize];
}

export default function RawMaterialMilling() {
    const { state, dispatch } = useRawMaterialControl();

    const initialValue = state.weightOnMillKg?.toString();

    const [millSize, setMillSize] = useState<MillSize>(getMillingMode(initialValue));

    const [initialized, setInitialized] = useState(false);
    const preserveCustomSelectionRef = useRef(false);

    const [customMillSize, setCustomMillSize] = useState(
        getMillingMode(initialValue) === "custom" ? initialValue || "" : ""
    );

    const [grindingBodiesType, setGrindingBodiesType] = useState(
        state.grindingBodiesType || GRINDING_BODIES_TYPES[0]
    );
    const [grindingBodiesComment, setGrindingBodiesComment] = useState(state.grindingBodiesComment ?? "");
    const [grindingBodies, setGrindingBodies] = useState("");
    const [alcohol, setAlcohol] = useState("");
    const [alcoholComment, setAlcoholComment] = useState(state.alcoholComment ?? "");
    const [hours, setHours] = useState(10);
    const [minutes, setMinutes] = useState(0);

    const syncMillingFields = (preset: MillingPreset) => {
        setGrindingBodies(preset.grindingBodies);
        setAlcohol(preset.alcohol);
        setHours(preset.grindTimeHours);
        setMinutes(preset.grindTimeMinutes);

        dispatch({ type: "SET_FIELD", payload: { field: "grindingBodies", value: preset.grindingBodies as any } });
        dispatch({ type: "SET_FIELD", payload: { field: "grindTimeHours", value: Number(preset.grindTimeHours) } });
        dispatch({ type: "SET_FIELD", payload: { field: "grindTimeMinutes", value: Number(preset.grindTimeMinutes) } });
        dispatch({ type: "SET_FIELD", payload: { field: "alcohol", value: preset.alcohol as any } });
    };

    const applyPreset = (nextMillSize: Exclude<MillSize, "custom">) => {
        preserveCustomSelectionRef.current = false;
        setMillSize(nextMillSize);
        dispatch({ type: "SET_FIELD", payload: { field: "weightOnMillKg", value: nextMillSize } });
        syncMillingFields(MILLING_PRESETS[nextMillSize]);
    };

    const enableCustomMode = () => {
        if (millSize === "custom") {
            return;
        }

        preserveCustomSelectionRef.current = true;
        setMillSize("custom");
        syncMillingFields(CUSTOM_MILLING_DEFAULTS);
    };

    useEffect(() => {
        const nextMillSize = preserveCustomSelectionRef.current ? "custom" : getMillingMode(state.weightOnMillKg);
        const nextWeightOnMill = state.weightOnMillKg?.toString() || "";

        setMillSize(nextMillSize);
        setCustomMillSize((currentValue) => {
            if (nextMillSize !== "custom") {
                return "";
            }

            return preserveCustomSelectionRef.current ? currentValue : nextWeightOnMill;
        });

        preserveCustomSelectionRef.current = false;
    }, [state.weightOnMillKg]);

    useEffect(() => {
        const defaults = getMillingDefaults(millSize);
        const nextGrindingBodies =
            state.grindingBodies === undefined || state.grindingBodies === null
                ? defaults.grindingBodies
                : state.grindingBodies.toString();

        setGrindingBodies(nextGrindingBodies);
        setAlcohol(normalizeDecimalValue(state.alcohol ?? defaults.alcohol));
        setHours(state.grindTimeHours ?? defaults.grindTimeHours);
        setMinutes(state.grindTimeMinutes ?? defaults.grindTimeMinutes);

        setInitialized(true);
    }, [millSize, state.grindingBodies, state.grindTimeHours, state.grindTimeMinutes, state.alcohol]);

    useEffect(() => {
        if (state.grindingBodiesType && state.grindingBodiesType !== grindingBodiesType) {
            setGrindingBodiesType(state.grindingBodiesType);
        }
    }, [state.grindingBodiesType, grindingBodiesType]);

    useEffect(() => {
        setGrindingBodiesComment(state.grindingBodiesComment ?? "");
    }, [state.grindingBodiesComment]);

    useEffect(() => {
        setAlcoholComment(state.alcoholComment ?? "");
    }, [state.alcoholComment]);

    useEffect(() => {
        if (!initialized) return;

        dispatch({ type: "SET_FIELD", payload: { field: "grindTimeHours", value: Number(hours) } });
        dispatch({ type: "SET_FIELD", payload: { field: "grindTimeMinutes", value: Number(minutes) } });
    }, [hours, minutes, initialized, dispatch]);

    useEffect(() => {
        if (!initialized || millSize !== "custom") return;

        dispatch({ type: "SET_FIELD", payload: { field: "weightOnMillKg", value: customMillSize } });
    }, [customMillSize, millSize, initialized, dispatch]);

    return (
        <div className={cardClass}>
            <div className="mb-2 text-sm font-bold">Milling</div>

            <div className="flex flex-col gap-4">
                {/* Row 1: Weight on mill, Custom, Grind time */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <Label className={fieldLabelClass}>Weight on mill</Label>
                        <RadioGroup
                            layout="horizontal"
                            value={millSize}
                            onChange={(_, data) => applyPreset(data.value as Exclude<MillSize, "custom">)}
                        >
                            <Radio value="0.25" label="0.25 L" />
                        </RadioGroup>
                    </div>

                    <div>
                        <Label className={fieldLabelClass}>Custom weight on mill</Label>
                        <div className="relative w-full">
                            <input
                                className={fieldClassName}
                                value={millSize === "custom" ? customMillSize : ""}
                                onFocus={enableCustomMode}
                                onChange={(e) => {
                                    const normalized = e.target.value.replace(".", ",");
                                    if (/^\d*(?:[,.]\d{0,4})?$/.test(normalized)) {
                                        preserveCustomSelectionRef.current = true;
                                        setCustomMillSize(normalized);
                                    }
                                }}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"> L </div>
                        </div>
                    </div>

                    <div>
                        <Label className={fieldLabelClass}>Grind time</Label>
                        <div className="flex gap-2">
                            <div className="relative w-full">
                                <input
                                    className={fieldClassName}
                                    value={hours}
                                    onChange={(e) => {
                                        const num = Number(e.target.value);
                                        if (num >= 0 && num <= 1024) {
                                            setHours(num);
                                        }
                                    }}
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"> h </span>
                            </div>
                            <div className="relative w-full">
                                <input
                                    className={fieldClassName}
                                    value={minutes}
                                    onChange={(e) => {
                                        const num = Number(e.target.value);
                                        if (num >= 0 && num <= 59) {
                                            setMinutes(num);
                                        }
                                    }}
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"> m</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2: Grinding bodies, Comment, Amount */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <Label className={fieldLabelClass}>Grinding bodies</Label>
                        <div className={fieldClassName + " p-0"}>
                            <CustomSelect
                                value={grindingBodiesType}
                                onChange={(e) => {
                                    const next = e.target.value || GRINDING_BODIES_TYPES[0];
                                    setGrindingBodiesType(next);
                                    dispatch({ type: "SET_FIELD", payload: { field: "grindingBodiesType", value: next } });
                                }}
                            >
                                {GRINDING_BODIES_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </CustomSelect>
                        </div>
                    </div>

                    <div>
                        <Label className={fieldLabelClass}>Comment</Label>
                        <input
                            className={fieldClassName}
                            value={grindingBodiesComment}
                            onChange={(e) => {
                                setGrindingBodiesComment(e.target.value);
                                dispatch({ type: "SET_FIELD", payload: { field: "grindingBodiesComment", value: e.target.value } });
                            }}
                            placeholder=""
                        />
                    </div>

                    <div>
                        <Label className={fieldLabelClass}>Amount</Label>
                        <div className="relative w-full">
                            <input
                                className={fieldClassName}
                                value={grindingBodies}
                                onChange={(e) => {
                                    const normalized = e.target.value.replace(".", ",");
                                    if (/^\d*(?:[,.]\d{0,4})?$/.test(normalized)) {
                                        setGrindingBodies(normalized);
                                        dispatch({ type: "SET_FIELD", payload: { field: "grindingBodies", value: normalized as any } });
                                    }
                                }}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"> kg </span>
                        </div>
                    </div>
                </div>

                {/* Row 3: Alcohol, Comment */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <Label className={fieldLabelClass}>Alcohol</Label>
                        <div className="relative w-full">
                            <input
                                className={fieldClassName}
                                value={alcohol}
                                onChange={(e) => {
                                    const normalized = e.target.value.replace(".", ",");
                                    if (/^\d*(?:[,.]\d{0,4})?$/.test(normalized)) {
                                        setAlcohol(normalized);
                                        dispatch({ type: "SET_FIELD", payload: { field: "alcohol", value: normalized as any } });
                                    }
                                }}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"> L </span>
                        </div>
                    </div>

                    <div>
                        <Label className={fieldLabelClass}>Comment</Label>
                        <input
                            className={fieldClassName}
                            value={alcoholComment}
                            onChange={(e) => {
                                setAlcoholComment(e.target.value);
                                dispatch({ type: "SET_FIELD", payload: { field: "alcoholComment", value: e.target.value } });
                            }}
                            placeholder=""
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
